import { AddCircleOutline } from '@mui/icons-material'
import { Box, styled, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useGetLocalityMaintenances } from 'src/api/maintenance'
import { AppRoutesNavigation } from 'src/app-routes'
import { CommonPageLayout } from 'src/components/common/CommonPageLayout'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { EmptyStatusCard } from 'src/components/mooring/EmptyStatusCard'
import { FlexSpinner } from 'src/components/Spinner'
import { useNavigation } from 'src/context/NavigationContext'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { MUIThemeColors } from 'src/mui-theme'
import { Flex } from 'src/styles/flexComponent'
import { getThemeColor } from 'src/utils'
import NoMaintenance from '../../../assets/svg/no-maintenances.svg'
import { CreateCustomMaintenanceDialog } from './CreateCustomMaintenanceDialog'
import { StyledCard } from './EquipmentMaintenanceContainer'
import MaintenanceTable from './MaintenanceTable'
import { getMaintenanceHeaderKeyList } from './maintenanceTableHelper'
interface LocalityMaintenanceContainerProps {
  onlyDaily?: boolean
}

const LocalityMaintenanceContainer: React.FC<LocalityMaintenanceContainerProps> = ({ onlyDaily }) => {
  const { t } = useTranslation(['maintenance'])
  const { navigateTo } = useNavigation()
  const { customerId, localityId } = useParams()
  const { showDialog } = useDialogCtx()

  const { data: localityMaintenances, isLoading } = useGetLocalityMaintenances(
    customerId as EntityId,
    localityId as EntityId,
    onlyDaily,
  )

  const onAdd = () => {
    showDialog(
      CreateCustomMaintenanceDialog,
      {
        componentProps: {
          customerId: customerId as EntityId,
          localityId: localityId as EntityId,
          isLocalityMaintenance: true,
        },
        opts: {
          maxWidth: 'sm',
          fullWidth: true,
        },
      },
      undefined,
      true,
    )
  }

  return (
    <CommonPageLayout
      titleSection={{ title: t('locality-maintenance', { ns: 'maintenance' }) }}
      topAction={
        !onlyDaily
          ? [
              <StyledPerformedButton
                key="performed"
                variant="outlined"
                buttonText={t('history', { ns: 'common' })}
                onClick={() =>
                  navigateTo(
                    AppRoutesNavigation.Localities.localityPerformedMaintenances(
                      customerId as EntityId,
                      localityId as EntityId,
                    ),
                  )
                }
              />,
              {
                buttonText: t('add-maintenance', { ns: 'maintenance' }),
                buttonIcon: <AddCircleOutline />,
                variant: 'outlined',
                shadowWeight: 0,
                buttonTextProps: {
                  fontSize: '18px',
                },
                onClick: onAdd,
              },
            ]
          : []
      }
    >
      <StyledCard
        className="p-3 mb-2"
        sx={{ display: 'flex', flexDirection: 'column', marginTop: '8px' }}
        variant={
          !onlyDaily
            ? 'elevation'
            : isLoading || (localityMaintenances && localityMaintenances.length > 0)
            ? 'elevation'
            : 'outlined'
        }
      >
        {isLoading ? (
          <FlexSpinner />
        ) : localityMaintenances && localityMaintenances.length > 0 ? (
          <>
            <Flex.Row paddingLeft={'60px'} paddingBottom={'12px'} paddingTop={'10px'}>
              {getMaintenanceHeaderKeyList(t).map(
                (item: { flex: number; width?: number; headerName: string }, index) => (
                  <Typography flex={item?.flex} width={item?.width} key={index}>
                    {item.headerName}
                  </Typography>
                ),
              )}
            </Flex.Row>
            <MaintenanceTable maintenances={localityMaintenances} onlyDaily={onlyDaily} isLocalityMaintenance={true} />
          </>
        ) : (
          <Box className="text-center">
            <EmptyStatusCard
              image={NoMaintenance}
              header={t('no-maintenance-title', { ns: 'maintenance' })}
              content={t('no-maintenance-content', { ns: 'maintenance' })}
              imageWidth="60px"
              headerFontSize="18px"
              contentFontSize="14px"
              contentTopMargin="0px"
              compact={true}
            />
          </Box>
        )}
      </StyledCard>
    </CommonPageLayout>
  )
}

export default LocalityMaintenanceContainer

export const StyledPerformedButton = styled(GridActionButton)(({ theme }) => ({
  height: '44px',
  width: 'auto',
  '& .MuiTypography-root': {
    variant: 'subtitle1',
    fontWeight: 500,
    fontSize: '16px',
    whiteSpace: 'nowrap',
    color: getThemeColor(theme, MUIThemeColors.black),
  },
}))
